import React, { useState, useEffect, useMemo, useRef } from 'react';
import { MapContainer, ImageOverlay, Polyline, Polygon, Tooltip, useMap } from 'react-leaflet';
import { CRS, latLngBounds } from 'leaflet';
import SpacesService from '../../api/spaces.service';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

const DEFAULT_FLOORPLAN = 'https://via.placeholder.com/1000x1000?text=No+Floorplan+Available';

function MapController({ bounds }) {
    const map = useMap();

    useEffect(() => {
        // Ensure map exists and has the fitBounds method
        if (map && bounds && typeof map.fitBounds === 'function') {
            try {
                map.fitBounds(bounds);
            } catch (e) {
                console.error("Leaflet fitBounds error:", e);
            }
        }
    }, [bounds, map]);
    
    return null;
}

export default function SimpleFloorplan({ spaceId, coordinates, hoveredCoordinates }) {
    console.log("in floorplan")
    const [floorplanUrl, setFloorplanUrl] = useState(null);
    const [bounds, setBounds] = useState(null);
    const [zones, setZones] = useState([]);
    const [palette, setPalette] = useState([[0, 0, 0]]); // Default black

    const assetColorMap = useRef(new Map());
    const nextColorIndex = useRef(0);

    useEffect(() => {
        fetch('/colors.json')
            .then(res => res.json())
            .then(data => setPalette(data));
    }, []);

    const getAssetColor = (assetName) => {
        if (assetColorMap.current.has(assetName)) return assetColorMap.current.get(assetName);
        if (palette.length === 0) return 'rgb(0,0,0)';
        const rgb = palette[nextColorIndex.current % palette.length];
        const colorStr = `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;
        assetColorMap.current.set(assetName, colorStr);
        nextColorIndex.current++;
        return colorStr;
    };

    const { processedPaths, processedMarkers, assetLegend } = useMemo(() => {
        if (!coordinates || !Array.isArray(coordinates) || palette.length === 0) {
            return { processedPaths: [], processedMarkers: [], assetLegend: [] };
        }

        const paths = {};
        const markers = coordinates
            .filter(item => item.spaceCoordinates && typeof item.spaceCoordinates.x === 'number')
            .map((item, index) => {
                const color = getAssetColor(item.asset);
                const pos = [item.spaceCoordinates.y, item.spaceCoordinates.x];

                if (!paths[item.asset]) {
                    paths[item.asset] = { name: item.asset, color, points: [] };
                }
                paths[item.asset].points.push(pos);

                return { ...item, pos, color, key: `${item.id}-${index}` };
            });

        // Determine Start and End for each path
        const assetGroups = Object.values(paths).map(path => {
            return {
                ...path,
                startPoint: path.points[0],
                endPoint: path.points[path.points.length - 1]
            };
        });

        return {
            processedPaths: assetGroups,
            processedMarkers: markers,
            assetLegend: assetGroups.map(p => ({ name: p.name, color: p.color }))
        };
    }, [coordinates, palette]);

    useEffect(() => {
        console.log("abc")
        if (!spaceId) return;
        console.log("abc2")
        SpacesService.getSpace(spaceId).then((res) => {
            const spaceData = res.data.data;
            
            // Image fetch with fallback logic
            SpacesService.getSpaceFloorplan(spaceId).then((imgRes) => {
                if (imgRes?.data && (imgRes.data instanceof Blob || imgRes.data.size > 0)) {
                    const blob = imgRes.data instanceof Blob ? imgRes.data : new Blob([imgRes.data]);
                    setFloorplanUrl(URL.createObjectURL(blob));
                } else {
                    setFloorplanUrl(DEFAULT_FLOORPLAN);
                }
            }).catch(() => {
                setFloorplanUrl(DEFAULT_FLOORPLAN);
            });

            console.log("abc3")
            const refCoords = spaceData.localCoordinates;
            if (refCoords && refCoords.length > 0) {
                let xMax = 0, yMax = 0;
                refCoords.forEach(([x, y]) => {
                    if (x > xMax) xMax = x; if (y > yMax) yMax = y;
                });
                setBounds(latLngBounds([0, 0], [yMax, xMax]));
            } else {
                // Bounds fallback so the map doesn't get stuck in loading
                setBounds(latLngBounds([0, 0], [1000, 1000]));
            }

            const floorId = spaceData.referenceSpace?.id || spaceId;
            SpacesService.getChildZones(floorId).then((zoneRes) => {
                const fetchedZones = (zoneRes.data.data || [])
                    .filter(z => z.localCoordinates && z.id !== spaceId)
                    .map(z => ({
                        id: z.id,
                        name: z.name,
                        coords: z.localCoordinates.map(([x, y]) => [y, x])
                    }));
                setZones(fetchedZones);
            });
        });
    }, [spaceId]);

    const hoverPoint = (hoveredCoordinates && typeof hoveredCoordinates.x === 'number')
        ? [hoveredCoordinates.y, hoveredCoordinates.x] : null;

    if (!bounds || !floorplanUrl) return <div style={{ padding: '20px' }}>Loading...</div>;

    // This component bypasses the problematic React-Leaflet CircleMarker wrapper
    function SafeCircleMarker({ center, radius, pathOptions, tooltip }) {
        const map = useMap();
        const markerRef = useRef(null);

        useEffect(() => {
            if (!map || !center) return;

            const marker = L.circleMarker(center, {
                radius,
                ...pathOptions
            });

            if (tooltip) {
                marker.bindTooltip(tooltip);
            }

            marker.addTo(map);
            markerRef.current = marker;

            return () => {
                marker.remove();
            };
        }, [map, center]);

        return null;
    }

    return (
        <div style={{ display: 'flex', gap: '20px', fontFamily: 'sans-serif' }}>
            {/* MAP CONTAINER */}
            <div style={{ flex: 1, height: 'calc(100vh - 250px)', width: '100%', overflow: 'hidden', border: '1px solid #ddd' }}>
                <MapContainer crs={CRS.Simple} zoomSnap={0} style={{ height: '100%', width: '100%', background: '#f4f4f4' }}>
                    <ImageOverlay url={floorplanUrl} bounds={bounds} />
                    {bounds && (
                        <Polygon
                            key="floorplan-outer-boundary"
                            positions={[
                                [bounds.getSouth(), bounds.getWest()], 
                                [bounds.getNorth(), bounds.getWest()], 
                                [bounds.getNorth(), bounds.getEast()], 
                                [bounds.getSouth(), bounds.getEast()]
                            ]}
                            pathOptions={{
                                color: '#3388ff',
                                weight: 3,
                                fillColor: '#3388ff',
                                fillOpacity: 0.15 
                            }}
                        />
                    )}
                    {zones.map((zone) => (
                        <Polygon key={zone.id} positions={zone.coords} pathOptions={{ color: '#b70b07', weight: 1, fillColor: '#b70b07', fillOpacity: 0.25 }}>
                            <Tooltip sticky>{zone.name}</Tooltip>
                        </Polygon>
                    ))}
                    {processedMarkers.map((marker) => (
                        <SafeCircleMarker key={marker.key} center={marker.pos} radius={4} pathOptions={{ fillColor: marker.color, fillOpacity: 1, color: marker.color, weight: 0.5 }} tooltip={marker.serialNumber} />
                    ))}
                    {processedPaths.map((path) => (
                        <React.Fragment key={`group-${path.name}`}>
                            <Polyline
                                positions={path.points}
                                pathOptions={{ color: path.color, weight: 1.5, opacity: 1 }}
                            />

                            {/* START MARKER */}
                            {path.startPoint && (
                                <SafeCircleMarker
                                    center={path.startPoint}
                                    radius={8}
                                    pathOptions={{ fillColor: path.color, fillOpacity: 1, color: path.color, weight: 2 }}
                                    tooltip={`Start: ${path.name}`}
                                />
                            )}

                            {/* END MARKER */}
                            {path.endPoint && (
                                <SafeCircleMarker
                                    center={path.endPoint}
                                    radius={10}
                                    pathOptions={{ fillColor: 'transparent', color: path.color, weight: 4 }}
                                    tooltip={`Current: ${path.name}`}
                                />
                            )}
                        </React.Fragment>
                    ))}

                    {hoverPoint && <SafeCircleMarker center={hoverPoint} radius={8} pathOptions={{ color: '#000', fillColor: '#d9ca26', fillOpacity: 1, weight: 2 }} />}
                    <MapController bounds={bounds} />
                </MapContainer>
            </div>

            {/* LEGEND SIDEBAR */}
            <div className='legend-asset'>
                {assetLegend.map(asset => (
                    <div key={asset.name} style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', fontSize: '14px', color: asset.color }}>
                        <span>{asset.name}, &nbsp;</span>
                    </div>
                ))}
            </div>
        </div>
    );
}